import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAppState } from '../context/AppStateContext';
import {
    MagnifyingGlassIcon,
    AcademicCapIcon,
    UsersIcon,
    ClipboardDocumentCheckIcon,
    UserIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    ChevronRightIcon,
    FunnelIcon,
    BookOpenIcon,
    XMarkIcon,
    ArrowDownTrayIcon,
    TableCellsIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';
import FilterDropdown from './common/FilterDropdown';
import LoadingSpinner from './common/LoadingSpinner';
import Modal from './common/Modal';
import GradeBook from './teacher/GradeBook';

const TeacherClassesGradingTab = ({ isStandalone = false }) => {
    const { get } = useApi();
    const { showToast } = useAppState();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [classes, setClasses] = useState([]);
    const [years, setYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [gradeLevels, setGradeLevels] = useState([]);
    
    // Filter States
    const [showFilters, setShowFilters] = useState(false);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [selectedGradeStream, setSelectedGradeStream] = useState('all');
    const [selectedLearningArea, setSelectedLearningArea] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all'); // all | Complete | In Progress | Pending
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCourse, setSelectedCourse] = useState(null);

    // Export Missing Marks Modal States
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportTerm, setExportTerm] = useState('');
    const [exportAssessmentType, setExportAssessmentType] = useState('all');
    const [exportExamType, setExportExamType] = useState('all');
    const [exportLearningArea, setExportLearningArea] = useState('all');
    const [exportGrade, setExportGrade] = useState('all');
    const [exportStream, setExportStream] = useState('all');
    const [exporting, setExporting] = useState(false);

    const hasInitializedFilters = useRef(false);

    // Fetch initial filter metadata: Academic Years, Terms, and CBC Grade Levels
    const fetchMetadata = useCallback(async () => {
        try {
            const [yearsData, termsData, gradesData] = await Promise.all([
                get('/api/academic-years/').catch(() => []),
                get('/api/academic-terms/').catch(() => []),
                get('/api/cbc/grade-levels/').catch(() => [])
            ]);

            const yearsList = Array.isArray(yearsData) ? yearsData : (yearsData?.results || []);
            const termsList = Array.isArray(termsData) ? termsData : (termsData?.results || []);
            const gradesList = Array.isArray(gradesData) ? gradesData : (gradesData?.results || []);

            setYears(yearsList);
            setTerms(termsList);
            setGradeLevels(gradesList);

            // Auto-initialize with current active year and term once
            if (!hasInitializedFilters.current) {
                const currentYear = yearsList.find(y => y.is_current);
                if (currentYear) {
                    setSelectedYear(currentYear.id.toString());
                }

                const currentTerm = termsList.find(t => t.is_current);
                if (currentTerm) {
                    setSelectedTerm(currentTerm.id.toString());
                    setExportTerm(currentTerm.id.toString());
                    if (!currentYear && currentTerm.year) {
                        setSelectedYear(currentTerm.year.id ? currentTerm.year.id.toString() : currentTerm.year.toString());
                    }
                } else if (termsList.length > 0) {
                    setExportTerm(termsList[0].id.toString());
                }
                hasInitializedFilters.current = true;
            }
        } catch (err) {
            console.error('Error loading filter metadata:', err);
        }
    }, [get]);

    // Fetch Classes & Grading data for the selected academic term and year
    const fetchClasses = useCallback(async () => {
        try {
            setLoading(true);
            let url = '/teachers/hos/classes-grading/';
            const params = [];
            
            if (selectedTerm && selectedTerm !== 'all') {
                params.push(`term_id=${selectedTerm}`);
            }
            if (selectedYear) {
                params.push(`year_id=${selectedYear}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await get(url);
            setClasses(response.classes || []);
        } catch (err) {
            console.error('Error fetching classes & grading:', err);
            showToast('Failed to load class grading data', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, selectedTerm, selectedYear, showToast]);

    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata]);

    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    // 1. Academic Year Options
    const yearOptions = useMemo(() => [
        { name: 'All Academic Years', value: '' },
        ...years.map(y => ({
            name: `${y.name || y.year || 'Year'} ${y.is_current ? '• (Current)' : ''}`.trim(),
            value: y.id.toString()
        }))
    ], [years]);

    // 2. Academic Term Options (Filtered by selected year if active)
    const filteredTermOptions = useMemo(() => {
        let termsList = terms;
        if (selectedYear) {
            termsList = terms.filter(t => {
                const termYearId = t.year?.id || t.year || t.academic_year_id;
                return String(termYearId) === String(selectedYear);
            });
        }

        return [
            { name: 'All Terms', value: 'all' },
            ...termsList.map(t => ({
                name: `${t.name || `Term ${t.term_number}`} ${t.is_current ? '• (Current)' : ''}`.trim(),
                value: t.id.toString()
            }))
        ];
    }, [terms, selectedYear]);

    // 3. Learning Area Options (Shared across Page Filter & Export Modal)
    const learningAreaOptions = useMemo(() => {
        const uniqueNames = Array.from(new Set(classes.map(c => c.name).filter(Boolean))).sort();
        return [
            { name: 'All Learning Areas', value: 'all' },
            ...uniqueNames.map(name => ({
                name,
                value: name
            }))
        ];
    }, [classes]);

    // 4. Grade & Stream Options (Guaranteed strictly unique values to prevent Headless UI infinite loops)
    const gradeStreamOptions = useMemo(() => {
        const list = [{ name: 'All Grades & Streams', value: 'all' }];
        const seenValues = new Set(['all']);

        if (Array.isArray(gradeLevels) && gradeLevels.length > 0) {
            const sortedGrades = [...gradeLevels].sort((a, b) => (a.order || 0) - (b.order || 0));
            sortedGrades.forEach(gl => {
                const gradeVal = `grade_${gl.id}`;
                if (!seenValues.has(gradeVal)) {
                    seenValues.add(gradeVal);
                    list.push({
                        name: `${gl.name} (All Streams)`,
                        value: gradeVal
                    });
                }
                if (Array.isArray(gl.streams) && gl.streams.length > 0) {
                    gl.streams.forEach(st => {
                        const streamVal = `stream_${gl.id}_${st.id}`;
                        if (!seenValues.has(streamVal)) {
                            seenValues.add(streamVal);
                            list.push({
                                name: `${gl.name} • Stream ${st.name}`,
                                value: streamVal
                            });
                        }
                    });
                }
            });
        } else {
            // Fallback derived directly from fetched classes data with grade-scoped uniqueness
            const gradeMap = new Map();
            classes.forEach(c => {
                if (c.grade_level_name) {
                    if (!gradeMap.has(c.grade_level_name)) {
                        gradeMap.set(c.grade_level_name, new Set());
                    }
                    if (c.stream_name) {
                        gradeMap.get(c.grade_level_name).add(c.stream_name);
                    }
                }
            });
            gradeMap.forEach((streamSet, gName) => {
                const gradeVal = `grade_name_${gName}`;
                if (!seenValues.has(gradeVal)) {
                    seenValues.add(gradeVal);
                    list.push({ name: `${gName} (All Streams)`, value: gradeVal });
                }
                streamSet.forEach(sName => {
                    const streamVal = `stream_name_${gName}_${sName}`;
                    if (!seenValues.has(streamVal)) {
                        seenValues.add(streamVal);
                        list.push({ name: `${gName} • Stream ${sName}`, value: streamVal });
                    }
                });
            });
        }

        return list;
    }, [gradeLevels, classes]);

    // Check if any non-default filter is applied
    const hasActiveFilters = selectedYear !== '' || (selectedTerm !== '' && selectedTerm !== 'all') || selectedGradeStream !== 'all' || selectedLearningArea !== 'all' || statusFilter !== 'all';

    const handleResetFilters = () => {
        setSelectedYear('');
        setSelectedTerm('all');
        setSelectedGradeStream('all');
        setSelectedLearningArea('all');
        setStatusFilter('all');
        setSearchQuery('');
    };

    // Client-side filtering for instantaneous reactivity without network churn
    const filteredClasses = useMemo(() => {
        return classes.filter(c => {
            if (statusFilter !== 'all' && c.status !== statusFilter) return false;

            if (selectedLearningArea !== 'all') {
                if (!c.name || c.name.toLowerCase() !== selectedLearningArea.toLowerCase()) {
                    return false;
                }
            }

            if (selectedGradeStream !== 'all') {
                if (selectedGradeStream.startsWith('stream_name_')) {
                    const parts = selectedGradeStream.replace('stream_name_', '').split('_');
                    const targetGrade = parts[0]?.toLowerCase();
                    const targetStream = parts.slice(1).join('_').toLowerCase();
                    if (targetGrade && c.grade_level_name && c.grade_level_name.toLowerCase() !== targetGrade) {
                        return false;
                    }
                    if (targetStream && (!c.stream_name || !c.stream_name.toLowerCase().includes(targetStream))) {
                        return false;
                    }
                } else if (selectedGradeStream.startsWith('grade_name_')) {
                    const target = selectedGradeStream.replace('grade_name_', '').toLowerCase();
                    if (!c.grade_level_name || c.grade_level_name.toLowerCase() !== target) return false;
                } else if (selectedGradeStream.startsWith('stream_')) {
                    const parts = selectedGradeStream.replace('stream_', '').split('_');
                    const streamId = parts.length > 1 ? parts[1] : parts[0];
                    const gradeId = parts.length > 1 ? parts[0] : null;

                    if (gradeId && c.grade_level_id && String(c.grade_level_id) !== String(gradeId)) {
                        return false;
                    }

                    const matchingStream = gradeLevels.flatMap(g => g.streams || []).find(s => String(s.id) === String(streamId));
                    if (matchingStream && c.stream_name && !c.stream_name.toLowerCase().includes(matchingStream.name.toLowerCase())) {
                        return false;
                    }
                } else if (selectedGradeStream.startsWith('grade_')) {
                    const gradeId = selectedGradeStream.replace('grade_', '');
                    if (c.grade_level_id && String(c.grade_level_id) !== String(gradeId)) {
                        const matchingGrade = gradeLevels.find(g => String(g.id) === String(gradeId));
                        if (matchingGrade && c.grade_level_name && c.grade_level_name.toLowerCase() !== matchingGrade.name.toLowerCase()) {
                            return false;
                        }
                    }
                }
            }

            if (searchQuery.trim()) {
                const s = searchQuery.toLowerCase();
                const matches = (
                    (c.name && c.name.toLowerCase().includes(s)) ||
                    (c.code && c.code.toLowerCase().includes(s)) ||
                    (c.teacher_name && c.teacher_name.toLowerCase().includes(s)) ||
                    (c.grade_level_name && c.grade_level_name.toLowerCase().includes(s)) ||
                    (c.stream_name && c.stream_name.toLowerCase().includes(s))
                );
                if (!matches) return false;
            }
            return true;
        });
    }, [classes, statusFilter, selectedGradeStream, selectedLearningArea, searchQuery, gradeLevels]);

    // KPI Aggregations
    const kpis = useMemo(() => {
        const total = classes.length;
        const complete = classes.filter(c => c.status === 'Complete').length;
        const inProgress = classes.filter(c => c.status === 'In Progress').length;
        const pending = classes.filter(c => c.status === 'Pending').length;
        return { total, complete, inProgress, pending };
    }, [classes]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Complete':
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-full text-[9px] font-black uppercase tracking-wider">
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        Complete
                    </span>
                );
            case 'In Progress':
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200/80 rounded-full text-[9px] font-black uppercase tracking-wider">
                        <ClockIcon className="w-3.5 h-3.5" />
                        In Progress
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200/80 rounded-full text-[9px] font-black uppercase tracking-wider">
                        <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                        Pending
                    </span>
                );
        }
    };

    // Ensure exportTerm always defaults to current term if loaded later
    useEffect(() => {
        if (!exportTerm && terms.length > 0) {
            const cur = terms.find(t => t.is_current);
            setExportTerm(cur ? cur.id.toString() : terms[0].id.toString());
        }
    }, [terms, exportTerm]);

    // Export Modal Term Options (Always clearly displays Current Term)
    const exportTermOptions = useMemo(() => [
        { name: 'All Academic Terms', value: 'all' },
        ...terms.map(t => ({
            name: `${t.name} ${t.is_current ? '• (Current)' : ''}`.trim(),
            value: t.id.toString()
        }))
    ], [terms]);

    // Export Modal Assessment Type Options
    const exportAssessmentTypeOptions = useMemo(() => [
        { name: 'All Assessment Types', value: 'all' },
        { name: 'Formative Assessments Only', value: 'formative' },
        { name: 'Summative Assessments Only', value: 'summative' }
    ], []);

    // Export Modal Exam Type Options (Conditional on Summative)
    const exportExamTypeOptions = useMemo(() => [
        { name: 'All Summative Exams', value: 'all' },
        { name: 'Mid-Term Exams Only', value: 'mid-term' },
        { name: 'End-Term Exams Only', value: 'end-term' }
    ], []);

    // Export Modal Grade Scope Options (Concise ~10-15 items)
    const exportGradeOptions = useMemo(() => {
        const list = [{ name: 'All Grades', value: 'all' }];
        if (Array.isArray(gradeLevels) && gradeLevels.length > 0) {
            const sortedGrades = [...gradeLevels].sort((a, b) => (a.order || 0) - (b.order || 0));
            sortedGrades.forEach(gl => {
                list.push({
                    name: gl.name,
                    value: String(gl.id)
                });
            });
        } else {
            const gradeNames = Array.from(new Set(classes.map(c => c.grade_level_name).filter(Boolean))).sort();
            gradeNames.forEach(gName => {
                list.push({
                    name: gName,
                    value: `name_${gName}`
                });
            });
        }
        return list;
    }, [gradeLevels, classes]);

    // Export Modal Stream Scope Options (Conditional on selected grade, concise ~2-4 items)
    const exportStreamOptions = useMemo(() => {
        if (exportGrade === 'all') return [{ name: 'All Streams', value: 'all' }];
        
        const list = [{ name: 'All Streams in Selected Grade', value: 'all' }];
        if (exportGrade.startsWith('name_')) {
            const targetGradeName = exportGrade.replace('name_', '').toLowerCase();
            const streams = Array.from(new Set(classes.filter(c => c.grade_level_name?.toLowerCase() === targetGradeName && c.stream_name).map(c => c.stream_name))).sort();
            streams.forEach(sName => {
                list.push({
                    name: `Stream ${sName}`,
                    value: `name_${sName}`
                });
            });
        } else {
            const selectedGl = gradeLevels.find(g => String(g.id) === String(exportGrade));
            if (selectedGl && Array.isArray(selectedGl.streams)) {
                selectedGl.streams.forEach(st => {
                    list.push({
                        name: `Stream ${st.name}`,
                        value: String(st.id)
                    });
                });
            }
        }
        return list;
    }, [exportGrade, gradeLevels, classes]);

    // Handler to export missing marks to Excel
    const handleExportExcel = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams();
            if (exportTerm && exportTerm !== 'all') {
                params.append('term_id', exportTerm);
            } else if (exportTerm === 'all') {
                params.append('term_id', 'all');
            }
            
            if (exportAssessmentType && exportAssessmentType !== 'all') {
                params.append('assessment_type', exportAssessmentType);
            }
            if (exportAssessmentType === 'summative' && exportExamType && exportExamType !== 'all') {
                params.append('exam_type', exportExamType);
            }
            if (exportLearningArea && exportLearningArea !== 'all') {
                params.append('learning_area', exportLearningArea);
            }
            
            // Handle cascading grade and stream filters
            if (exportStream && exportStream !== 'all') {
                if (exportStream.startsWith('name_')) {
                    const gradeName = exportGrade.replace('name_', '');
                    const streamName = exportStream.replace('name_', '');
                    params.append('grade_stream', `stream_name_${gradeName}_${streamName}`);
                } else {
                    params.append('grade_stream', `stream_${exportGrade}_${exportStream}`);
                }
            } else if (exportGrade && exportGrade !== 'all') {
                if (exportGrade.startsWith('name_')) {
                    params.append('grade_stream', `grade_name_${exportGrade.replace('name_', '')}`);
                } else {
                    params.append('grade_stream', `grade_${exportGrade}`);
                }
            }
            
            params.append('export_format', 'excel');

            const endpoint = `/teachers/hos/missing-marks-export/?${params.toString()}`;
            const blob = await get(endpoint, { responseType: 'blob' });

            const blobUrl = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = blobUrl;

            const termObj = terms.find(t => String(t.id) === String(exportTerm));
            const termPart = termObj ? termObj.name.replace(/\s+/g, '_') : (exportTerm === 'all' ? 'All_Terms' : 'Current_Term');
            const subjectPart = exportLearningArea !== 'all' ? `_${exportLearningArea.replace(/\s+/g, '_')}` : '';
            const typePart = exportAssessmentType !== 'all' ? `_${exportAssessmentType}` : '';
            const examPart = (exportAssessmentType === 'summative' && exportExamType !== 'all') ? `_${exportExamType}` : '';
            const dateStr = new Date().toISOString().split('T')[0];
            const finalFilename = `Missing_Marks_${termPart}${subjectPart}${typePart}${examPart}_${dateStr}.xlsx`;

            link.setAttribute('download', finalFilename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            showToast('Missing marks spreadsheet exported successfully', 'success');
            setShowExportModal(false);
        } catch (err) {
            console.error('Export error:', err);
            let message = 'Failed to export missing marks. Please try again.';
            const data = err?.response?.data;
            if (data instanceof Blob && data.type && data.type.includes('json')) {
                try {
                    const parsed = JSON.parse(await data.text());
                    message = parsed.error || parsed.detail || message;
                } catch (parseErr) {
                    // keep generic message if the error body isn't parseable JSON
                }
            }
            showToast(message, 'error');
        } finally {
            setExporting(false);
        }
    };

    const content = (
        <div className="space-y-6">
            {/* Header with Search and Single Filters Toggle Button */}
            <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                <div>
                    <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight">
                        Classes &amp; Grading
                    </h2>
                    <p className="text-xs text-slate-500 font-medium mt-1">
                        School-wide tracking of subject progress, enrolled scholars, and assessment marks completion.
                    </p>
                </div>

                {/* Right Controls: Filters Button stacked above Search Input */}
                <div className="flex flex-col sm:items-end gap-2.5 w-full lg:w-auto">
                    {/* Top Row: Export, Filters Button, and Refresh */}
                    <div className="flex items-center gap-2">
                        {/* Export Missing Marks Button (to the left of Filters) */}
                        <button
                            type="button"
                            onClick={() => setShowExportModal(true)}
                            className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 border bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 transition-all active:scale-95 shadow-sm shadow-emerald-900/10"
                            title="Export Missing Marks to Excel"
                        >
                            <ArrowDownTrayIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                            <span>Export</span>
                        </button>

                        {/* Single "Filters" Toggle Button (Matching Character Registry Design) */}
                        <button
                            type="button"
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 border transition-all active:scale-95 shadow-sm ${
                                showFilters || hasActiveFilters
                                    ? 'bg-[#18216D] text-white border-[#18216D]'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-3.5 h-3.5" />
                            <span>Filters</span>
                            {hasActiveFilters && (
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            )}
                        </button>

                        {/* Refresh Data Icon Button */}
                        <button
                            onClick={fetchClasses}
                            className="p-2 bg-white hover:bg-slate-50 text-[#18216D] rounded-xl border border-slate-200/80 transition-all active:scale-95 shadow-sm"
                            title="Refresh Data"
                        >
                            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    {/* Bottom Row: Header Search Input */}
                    <div className="relative w-full sm:w-72">
                        <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search subject, teacher, grade..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-50 hover:bg-white border border-slate-200/80 rounded-xl text-xs font-bold text-[#18216D] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* Pop-Out Filters Drawer (Matching Character Registry Pattern) */}
            {showFilters && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                Filter Classes &amp; Teachers
                            </span>
                            {hasActiveFilters && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                    Filtered
                                </span>
                            )}
                        </div>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* 1. Academic Year Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Year
                            </label>
                            <FilterDropdown
                                value={selectedYear}
                                onChange={(val) => {
                                    setSelectedYear(val || '');
                                    setSelectedTerm('all'); // Reset term when year changes
                                }}
                                options={yearOptions}
                                placeholder="All Academic Years"
                                className="w-full"
                            />
                        </div>

                        {/* 2. Academic Term Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Term
                            </label>
                            <FilterDropdown
                                value={selectedTerm}
                                onChange={(val) => setSelectedTerm(val || 'all')}
                                options={filteredTermOptions}
                                placeholder="All Terms"
                                className="w-full"
                            />
                        </div>

                        {/* 3. Grade & Stream Combined Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Grade &amp; Stream
                            </label>
                            <FilterDropdown
                                value={selectedGradeStream}
                                onChange={(val) => setSelectedGradeStream(val || 'all')}
                                options={gradeStreamOptions}
                                placeholder="All Grades &amp; Streams"
                                className="w-full"
                            />
                        </div>

                        {/* 4. Learning Area Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Learning Area
                            </label>
                            <FilterDropdown
                                value={selectedLearningArea}
                                onChange={(val) => setSelectedLearningArea(val || 'all')}
                                options={learningAreaOptions}
                                placeholder="All Learning Areas"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Interactive KPI Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                    type="button"
                    onClick={() => setStatusFilter('all')}
                    className={`text-left bg-white rounded-2xl p-5 border transition-all shadow-sm flex items-center justify-between group ${
                        statusFilter === 'all' ? 'border-[#18216D] ring-2 ring-[#18216D]/10' : 'border-slate-100 hover:border-slate-200'
                    }`}
                >
                    <div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Total Classes</span>
                        <span className="text-2xl font-black text-[#18216D] tracking-tight">{kpis.total}</span>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 text-[#18216D] flex items-center justify-center group-hover:scale-105 transition-transform">
                        <AcademicCapIcon className="w-5 h-5" />
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === 'Complete' ? 'all' : 'Complete')}
                    className={`text-left bg-white rounded-2xl p-5 border transition-all shadow-sm flex items-center justify-between group ${
                        statusFilter === 'Complete' ? 'border-emerald-500 ring-2 ring-emerald-500/10' : 'border-slate-100 hover:border-slate-200'
                    }`}
                >
                    <div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Fully Graded</span>
                        <span className="text-2xl font-black text-emerald-600 tracking-tight">{kpis.complete}</span>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <CheckCircleIcon className="w-5 h-5" />
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === 'In Progress' ? 'all' : 'In Progress')}
                    className={`text-left bg-white rounded-2xl p-5 border transition-all shadow-sm flex items-center justify-between group ${
                        statusFilter === 'In Progress' ? 'border-amber-500 ring-2 ring-amber-500/10' : 'border-slate-100 hover:border-slate-200'
                    }`}
                >
                    <div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">In Progress</span>
                        <span className="text-2xl font-black text-amber-600 tracking-tight">{kpis.inProgress}</span>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <ClockIcon className="w-5 h-5" />
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === 'Pending' ? 'all' : 'Pending')}
                    className={`text-left bg-white rounded-2xl p-5 border transition-all shadow-sm flex items-center justify-between group ${
                        statusFilter === 'Pending' ? 'border-rose-500 ring-2 ring-rose-500/10' : 'border-slate-100 hover:border-slate-200'
                    }`}
                >
                    <div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Pending Grading</span>
                        <span className="text-2xl font-black text-rose-600 tracking-tight">{kpis.pending}</span>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <ExclamationTriangleIcon className="w-5 h-5" />
                    </div>
                </button>
            </div>

            {/* Classes Cards Grid */}
            {loading ? (
                <div className="py-24 flex flex-col items-center justify-center">
                    <LoadingSpinner />
                    <p className="mt-4 text-xs font-black text-indigo-300 uppercase tracking-widest animate-pulse">
                        Loading Classes &amp; Progress...
                    </p>
                </div>
            ) : filteredClasses.length === 0 ? (
                <div className="py-24 text-center bg-white rounded-[2.5rem] border border-slate-100">
                    <AcademicCapIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <h3 className="text-lg font-black text-slate-400 uppercase tracking-wider">No Classes Found</h3>
                    <p className="text-xs text-slate-400 mt-1">Try changing the term or search filters.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredClasses.map(cls => (
                        <div
                            key={`cls-${cls.id}`}
                            className="bg-white rounded-[2rem] border border-slate-100 p-6 shadow-sm hover:shadow-xl hover:shadow-indigo-900/5 hover:border-indigo-100 transition-all flex flex-col justify-between group"
                        >
                            <div>
                                {/* Top Badges */}
                                <div className="flex items-center justify-between gap-2 mb-4">
                                    <div className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-full text-[9px] font-black text-[#18216D] uppercase tracking-wider truncate">
                                        {cls.grade_level_name ? `${cls.grade_level_name}` : 'General'}
                                        {cls.stream_name ? ` • ${cls.stream_name}` : ''}
                                    </div>
                                    {getStatusBadge(cls.status)}
                                </div>

                                {/* Class Title & Code */}
                                <h3 className="text-xl font-black text-[#18216D] tracking-tight line-clamp-1 mb-1 group-hover:text-indigo-600 transition-colors">
                                    {cls.name}
                                </h3>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-4">
                                    Code: {cls.code || 'N/A'}
                                </span>

                                {/* Teacher & Enrolled Info */}
                                <div className="space-y-2 mb-5">
                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                                        <UserIcon className="w-4 h-4 text-indigo-400 shrink-0" />
                                        <span className="truncate">{cls.teacher_name}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs font-bold text-slate-500">
                                        <div className="flex items-center gap-2">
                                            <UsersIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                            <span>{cls.students_count} Scholars</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <ClipboardDocumentCheckIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                            <span>{cls.assignments_count} Tasks</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="space-y-1.5 mb-6">
                                    <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider">
                                        <span className="text-slate-400">Grading Progress</span>
                                        <span className="text-[#18216D]">{cls.progress_percentage}%</span>
                                    </div>
                                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-500 rounded-full ${
                                                cls.status === 'Complete'
                                                    ? 'bg-emerald-500'
                                                    : cls.status === 'In Progress'
                                                    ? 'bg-gradient-to-r from-amber-400 to-[#18216D]'
                                                    : 'bg-rose-400'
                                            }`}
                                            style={{ width: `${Math.min(cls.progress_percentage, 100)}%` }}
                                        />
                                    </div>
                                    <div className="text-[9px] font-bold text-slate-400 text-right">
                                        {cls.graded_count} of {cls.expected_count} marks recorded
                                    </div>
                                </div>
                            </div>

                            {/* Action Button */}
                            <button
                                onClick={() => setSelectedCourse(cls)}
                                className="w-full py-3 px-4 bg-[#18216D] hover:bg-[#0D164F] text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-md transition-all flex items-center justify-center gap-2 group-hover:shadow-indigo-900/20 active:scale-95"
                            >
                                <span>Inspect GradeBook</span>
                                <ChevronRightIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Interactive GradeBook Modal */}
            {selectedCourse && (
                <Modal
                    isOpen={!!selectedCourse}
                    onClose={() => {
                        setSelectedCourse(null);
                        fetchClasses(); // Refresh counts after closing
                    }}
                    maxWidth="max-w-7xl"
                    header={
                        <div className="relative bg-white px-7 py-5 border-b border-slate-100 flex items-center justify-between z-10 flex-shrink-0">
                            <div className="flex items-center gap-3.5 sm:gap-4">
                                <div className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#18216D] shadow-sm flex-shrink-0">
                                    <BookOpenIcon className="w-5 h-5 text-[#18216D]" />
                                </div>
                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="text-lg sm:text-xl font-black text-[#18216D] tracking-tight">
                                            {selectedCourse.name}
                                            {selectedCourse.grade_level_name ? ` — ${selectedCourse.grade_level_name}` : ''}
                                            {selectedCourse.stream_name ? ` (${selectedCourse.stream_name})` : ''}
                                        </h3>
                                        <span className="px-2.5 py-0.5 bg-blue-50 text-[#18216D] border border-blue-200/80 rounded-full text-[9px] font-black uppercase tracking-wider">
                                            GradeBook
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs font-semibold text-slate-500">
                                        <span className="flex items-center gap-1 text-slate-700 font-bold">
                                            <UserIcon className="w-3.5 h-3.5 text-indigo-500" />
                                            <span>Teacher: <strong className="text-[#18216D] font-black">{selectedCourse.teacher_name || 'Unassigned'}</strong></span>
                                        </span>
                                        <span className="text-slate-300 hidden sm:inline">•</span>
                                        <span className="inline-flex items-center gap-1 text-emerald-700 font-black bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 text-[11px]">
                                            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                                            <span>{selectedCourse.graded_students_count ?? 0} Graded</span>
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-rose-700 font-black bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100 text-[11px]">
                                            <ExclamationTriangleIcon className="w-3.5 h-3.5 text-rose-500" />
                                            <span>{selectedCourse.ungraded_students_count ?? Math.max(0, (selectedCourse.students_count || 0) - (selectedCourse.graded_students_count || 0))} Not Graded</span>
                                        </span>
                                        <span className="text-slate-300 hidden sm:inline">•</span>
                                        <span className="text-slate-500 font-bold text-[11px]">
                                            {selectedCourse.students_count || 0} Scholars Total
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setSelectedCourse(null);
                                    fetchClasses();
                                }}
                                className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-500 flex items-center justify-center transition-all group flex-shrink-0 ml-3 border border-slate-100"
                                title="Close GradeBook"
                            >
                                <XMarkIcon className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </div>
                    }
                >
                    <div className="p-2">
                        <GradeBook
                            courseId={selectedCourse.id}
                            selectedTerm={selectedTerm !== 'all' ? selectedTerm : undefined}
                        />
                    </div>
                </Modal>
            )}

            {/* Export Missing Marks Modal */}
            {showExportModal && (
                <Modal
                    isOpen={showExportModal}
                    onClose={() => !exporting && setShowExportModal(false)}
                    maxWidth="max-w-2xl"
                    header={
                        <div className="relative bg-white px-7 py-5 border-b border-slate-100 flex items-center justify-between z-10 flex-shrink-0">
                            <div className="flex items-center gap-3.5">
                                <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm flex-shrink-0">
                                    <TableCellsIcon className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-[#18216D] tracking-tight uppercase">
                                        Export Missing Marks Audit
                                    </h3>
                                    <p className="text-xs text-slate-500 font-medium mt-0.5">
                                        Export an Excel sheet of students missing grades per assessment
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => !exporting && setShowExportModal(false)}
                                className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-500 flex items-center justify-center transition-all group flex-shrink-0 ml-3 border border-slate-100"
                                title="Close"
                            >
                                <XMarkIcon className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </div>
                    }
                >
                    <div className="p-6 space-y-5">
                        {/* 1. Academic Term (Defaults to Current Term) */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label className="text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                    Academic Term
                                </label>
                                <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                    Defaults to Current Term
                                </span>
                            </div>
                            <FilterDropdown
                                value={exportTerm}
                                onChange={setExportTerm}
                                options={exportTermOptions}
                                placeholder="Select Term"
                                className="w-full"
                            />
                        </div>

                        {/* 2. Assessment Type & Conditional Summative Exam Period (Side by Side) */}
                        <div className={`grid gap-4 ${exportAssessmentType === 'summative' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                    Assessment Type
                                </label>
                                <FilterDropdown
                                    value={exportAssessmentType}
                                    onChange={(val) => {
                                        setExportAssessmentType(val);
                                        if (val !== 'summative') setExportExamType('all');
                                    }}
                                    options={exportAssessmentTypeOptions}
                                    placeholder="Select Assessment Type"
                                    className="w-full"
                                />
                            </div>

                            {exportAssessmentType === 'summative' && (
                                <div className="space-y-1.5 animate-fadeIn">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                        Summative Exam Period
                                    </label>
                                    <FilterDropdown
                                        value={exportExamType}
                                        onChange={setExportExamType}
                                        options={exportExamTypeOptions}
                                        placeholder="Select Exam Period"
                                        className="w-full"
                                    />
                                </div>
                            )}
                        </div>

                        {/* 3. Learning Area & Grade Scope (Side by Side) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                    Learning Area / Subject
                                </label>
                                <FilterDropdown
                                    value={exportLearningArea}
                                    onChange={setExportLearningArea}
                                    options={learningAreaOptions}
                                    placeholder="Select Learning Area"
                                    className="w-full"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                    Grade Scope
                                </label>
                                <FilterDropdown
                                    value={exportGrade}
                                    onChange={(val) => {
                                        setExportGrade(val || 'all');
                                        setExportStream('all');
                                    }}
                                    options={exportGradeOptions}
                                    placeholder="Select Grade Scope"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* 4. Stream Scope (Conditional on specific grade selection) */}
                        {exportGrade !== 'all' && (
                            <div className="space-y-1.5 animate-fadeIn">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                        Stream Scope (Optional)
                                    </label>
                                    <span className="text-[9px] font-bold text-slate-400">
                                        Defaults to All Streams in selected grade
                                    </span>
                                </div>
                                <FilterDropdown
                                    value={exportStream}
                                    onChange={(val) => setExportStream(val || 'all')}
                                    options={exportStreamOptions}
                                    placeholder="Select Stream"
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowExportModal(false)}
                                disabled={exporting}
                                className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleExportExcel}
                                disabled={exporting}
                                className="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-900/20 active:scale-95 transition-all disabled:opacity-50"
                            >
                                {exporting ? (
                                    <>
                                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                        <span>Generating Excel...</span>
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="w-4 h-4 stroke-[2.5]" />
                                        <span>Download Excel (.xlsx)</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );

    if (isStandalone) {
        return (
            <div className="min-h-screen bg-slate-50">
                <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="mb-4">
                        <button
                            onClick={() => navigate('/teacher/hod-dashboard')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-100 text-[#18216D] border border-slate-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm active:scale-95 group"
                        >
                            <ArrowLeftIcon className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform text-slate-400 group-hover:text-[#18216D]" />
                            <span>Back to HOD Dashboard</span>
                        </button>
                    </div>
                    {content}
                </main>
            </div>
        );
    }

    return content;
};

export default TeacherClassesGradingTab;